import React from "react";
import { ArrowClockwise, CheckCircle, Clock, Pause, Warning, XCircle } from "@phosphor-icons/react";

export const BTN = "border border-black px-2 py-1 text-[10px] uppercase tracking-widest transition-colors duration-100 hover:bg-black hover:text-white disabled:opacity-50";
export const BTN_PRIMARY = "border border-black bg-black text-white px-3 py-1.5 text-xs uppercase tracking-widest transition-colors duration-100 hover:bg-zinc-800 disabled:opacity-50";

const STATUS = {
  pending: { label: "Waiting for approval", cls: "bg-amber-100 border-amber-600 text-amber-900", icon: Clock },
  approved: { label: "Live", cls: "bg-emerald-100 border-emerald-700 text-emerald-900", icon: CheckCircle },
  paused: { label: "Paused", cls: "bg-zinc-100 border-zinc-500 text-zinc-700", icon: Pause },
  rejected: { label: "Rejected", cls: "bg-red-100 border-red-700 text-red-900", icon: XCircle },
  disabled: { label: "Turned off by admin", cls: "bg-zinc-200 border-zinc-700 text-zinc-800", icon: Pause },
};

export const shownStatus = (row) => (row.status === "approved" && !row.enabled ? "paused" : row.status);

export function StatusBadge({ row, testId }) {
  const key = shownStatus(row);
  const s = STATUS[key] || STATUS.pending;
  const Icon = s.icon;
  return (
    <span data-testid={testId} data-status={key}
      className={`inline-flex items-center gap-1 border px-1.5 py-0.5 text-[10px] uppercase tracking-widest whitespace-nowrap ${s.cls}`}>
      <Icon size={11} weight="bold" /> {s.label}
    </span>
  );
}

export function EventChips({ events, labels, testId }) {
  return (
    <div className="flex flex-wrap gap-1" data-testid={testId}>
      {events.map((e) => (
        <span key={e} className="border border-black px-1.5 py-0.5 text-[10px] uppercase tracking-widest bg-white" title={labels?.[e]}>
          {e}
        </span>
      ))}
    </div>
  );
}

const fmt = (iso) => new Date(iso).toLocaleString();

// Red "keeps failing" chip (all retries used up) or amber "retrying" chip while retries are queued.
export function HealthBadge({ row, testId }) {
  if (row.failing_since) {
    return (
      <span data-testid={testId} data-health="failing" title={row.last_failure?.error || ""}
        className="inline-flex items-center gap-1 border border-red-700 bg-red-600 text-white px-1.5 py-0.5 text-[10px] uppercase tracking-widest whitespace-nowrap">
        <Warning size={11} weight="fill" /> Failing since {fmt(row.failing_since)}
      </span>
    );
  }
  if (row.retrying?.count) {
    return (
      <span data-testid={testId} data-health="retrying"
        className="inline-flex items-center gap-1 border border-amber-700 bg-amber-100 text-amber-900 px-1.5 py-0.5 text-[10px] uppercase tracking-widest whitespace-nowrap">
        <ArrowClockwise size={11} weight="bold" />
        {row.retrying.count === 1 ? "1 send retrying" : `${row.retrying.count} sends retrying`} · next {fmt(row.retrying.next_at)}
      </span>
    );
  }
  return null;
}

// Big red alert listing postbacks whose sends keep failing. Clears once a send works.
export function FailingAlert({ rows, showOwner = false, testId, children }) {
  if (!rows.length) {
    return null;
  }
  return (
    <div role="alert" data-testid={testId} className="border-2 border-red-700 bg-red-50 p-4 sm:p-5">
      <div className="flex items-center gap-2 text-red-800 font-chivo font-black uppercase tracking-tight text-lg">
        <Warning size={20} weight="fill" />
        {rows.length === 1 ? "1 postback keeps failing" : `${rows.length} postbacks keep failing`}
      </div>
      <ul className="mt-2 space-y-1 text-sm text-red-900">
        {rows.map((r) => (
          <li key={r.id} data-testid={`${testId}-item-${r.id}`}>
            <strong>{r.name}</strong>{showOwner && ` (${r.owner_name})`}: {r.last_failure?.error || "no answer"}
            <span className="text-red-700 text-xs"> · since {fmt(r.failing_since)}</span>
          </li>
        ))}
      </ul>
      <p className="text-xs text-red-800 mt-2">
        {rows.some((r) => r.last_failure?.attempt)
          ? "We tried again about 5 minutes, 30 minutes, 2 hours and 6 hours later."
          : "This kind of error won't fix itself, so we didn't try again."}
        {" "}The alert clears as soon as a send works.
      </p>
      {children}
    </div>
  );
}

export function LastFire({ fire, testId }) {
  if (!fire) {
    return <span className="text-[11px] text-zinc-500" data-testid={testId}>Not sent yet</span>;
  }
  return (
    <span data-testid={testId} data-ok={fire.ok ? "yes" : "no"} title={fire.error || ""}
      className={`text-[11px] font-mono ${fire.ok ? "text-emerald-700" : "text-red-700"}`}>
      {fire.ok ? "OK" : "Failed"} {fire.status_code ? `(${fire.status_code})` : ""} · {new Date(fire.at).toLocaleString()}
    </span>
  );
}
